import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Skeleton,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  CalendarMonth,
  CheckCircle,
  OpenInNew,
  ShoppingCart,
  LocalOffer,
  WorkspacePremium,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  getMySubscriptions,
  getPlanCatalog,
  isSubscriptionAccessActive,
} from '../services/subscriptionService';
import { openBillingPortal } from '../services/billingService';
import { redeemPromoCode } from '../services/promoCodesService';
import { useAuth } from '../context/AuthContext';
import colors from '../design-system/colors';

const STATUS_COLOR = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  canceled: 'default',
  unpaid: 'error',
  incomplete: 'warning',
  incomplete_expired: 'error',
};

const STATUS_LABEL = {
  active: 'Activa',
  trialing: 'En prueba',
  past_due: 'Pago pendiente',
  canceled: 'Cancelada',
  unpaid: 'Sin pagar',
  incomplete: 'Incompleta',
  incomplete_expired: 'Expirada',
};

export default function LicenseManagementPageModern() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useAuth();

  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [managingId, setManagingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [subs, catalog] = await Promise.all([getMySubscriptions(), getPlanCatalog()]);
      setSubscriptions(subs);
      setPlans(catalog);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las licencias.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const planByKey = useMemo(() => {
    const map = {};
    plans.forEach((p) => { map[`${p.scope}:${p.plan_key}`] = p; });
    return map;
  }, [plans]);

  const getPlan = (sub) =>
    planByKey[`${sub.scope}:${sub.plan_key}`] || null;

  const activeSubscriptions = useMemo(
    () => subscriptions.filter(isSubscriptionAccessActive),
    [subscriptions],
  );
  const historicalSubscriptions = useMemo(
    () => subscriptions.filter((s) => !isSubscriptionAccessActive(s)),
    [subscriptions],
  );

  const handleManage = async (sub) => {
    setManagingId(sub.id);
    try {
      await openBillingPortal();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo abrir la facturación.', severity: 'error' });
    } finally {
      setManagingId(null);
    }
  };

  const handleRedeemPromo = async (event) => {
    event.preventDefault();
    const code = promoCode.trim();
    if (!code || !user?.id) return;
    setRedeemingPromo(true);
    try {
      const result = await redeemPromoCode({
        code,
        userId: user.id,
        userEmail: user.email,
      });
      setPromoCode('');
      setSnackbar({
        open: true,
        message: result.extensionApplied
          ? `Código aplicado: licencia ampliada hasta ${new Date(result.newPeriodEnd).toLocaleDateString('es-ES')}.`
          : `Código aplicado y registrado para tu cuenta: ${result.durationLabel}.`,
        severity: 'success',
      });
      await load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo aplicar el código promocional.', severity: 'error' });
    } finally {
      setRedeemingPromo(false);
    }
  };

  const renderSubscriptionCard = (sub) => {
    const plan = getPlan(sub);
    const displayName = plan?.display_name
      || (sub.plan_key === 'trial-full' ? 'Prueba gratuita de un mes' : sub.plan_key);
    const isActive = isSubscriptionAccessActive(sub);

    return (
      <Card
        key={sub.id}
        sx={{
          borderRadius: 3,
          border: 1, borderColor: 'divider',
          '&:hover': { boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems={isMobile ? 'flex-start' : 'center'}
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <WorkspacePremium color={isActive ? 'primary' : 'disabled'} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {displayName}
              </Typography>
              <Chip
                size="small"
                label={STATUS_LABEL[sub.status] || sub.status}
                color={STATUS_COLOR[sub.status] || 'default'}
              />
              <Chip
                size="small"
                label={sub.scope === 'club' ? 'Club' : 'Individual'}
                variant="outlined"
              />
            </Stack>
            {sub.stripe_customer_id && (
              <Button
                size="small"
                variant="outlined"
                endIcon={managingId === sub.id ? <CircularProgress size={14} /> : <OpenInNew />}
                disabled={managingId === sub.id}
                onClick={() => handleManage(sub)}
              >
                Gestionar en Stripe
              </Button>
            )}
          </Stack>

          <Stack direction="row" spacing={3} flexWrap="wrap">
            {sub.current_period_end && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <CalendarMonth sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {sub.cancel_at_period_end ? 'Cancela' : 'Renueva'}:{' '}
                  {new Date(sub.current_period_end).toLocaleDateString('es-ES', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Typography>
              </Stack>
            )}
            {sub.scope === 'club' && sub.seats_total != null && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Plazas: {sub.seats_total}
              </Typography>
            )}
          </Stack>

          {plan?.features?.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 2 }}>
              {plan.features.slice(0, 4).map((feature) => (
                <Stack key={feature} direction="row" spacing={0.75} alignItems="flex-start">
                  <CheckCircle color="success" sx={{ fontSize: 16, mt: 0.2 }} />
                  <Typography variant="caption">{feature}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Box className="wp-page" sx={{ flex: 1, py: 6, px: 2, maxWidth: 900, mx: 'auto', width: '100%' }}>
      {/* Header */}
      <Box className="wp-heading" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
            Mis licencias
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Gestiona tus suscripciones y consulta los límites de tu plan.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<ShoppingCart />}
          onClick={() => navigate('/licencias')}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            backgroundColor: 'primary.main',
          }}
        >
          Ver planes
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card component="form" onSubmit={handleRedeemPromo} sx={{ mb: 4, borderRadius: 3, border: 1, borderColor: 'secondary.main', backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 213, 79, 0.12)' : theme.palette.secondary.light }}>
        <CardContent sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <LocalOffer sx={{ color: colors.secondary[700] }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                ¿Tienes un código promocional?
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Introdúcelo para activar el beneficio en tu cuenta.
              </Typography>
            </Box>
            <TextField
              className="wp-promo-code"
              size="small"
              label="Código promocional"
              value={promoCode}
              onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
              inputProps={{ 'aria-label': 'Código promocional' }}
              disabled={redeemingPromo}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!promoCode.trim() || redeemingPromo}
              startIcon={redeemingPromo ? <CircularProgress size={16} color="inherit" /> : <LocalOffer />}
            >
              Aplicar código
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: 3 }} />
          ))}
        </Box>
      )}

      {/* Empty state */}
      {!loading && subscriptions.length === 0 && (
        <Card sx={{ borderRadius: 3, textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
              Sin licencias activas
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Aún no tienes ninguna suscripción. Consulta los planes disponibles.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/licencias')}>
              Ver planes
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active subscriptions */}
      {!loading && activeSubscriptions.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Licencias activas
          </Typography>
          <Stack spacing={2}>
            {activeSubscriptions.map(renderSubscriptionCard)}
          </Stack>
        </Box>
      )}

      {/* Historical subscriptions */}
      {!loading && historicalSubscriptions.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: 'text.secondary' }}>
            Historial
          </Typography>
          <Stack spacing={2}>
            {historicalSubscriptions.map(renderSubscriptionCard)}
          </Stack>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
